import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FolderOpen, Plus, Users, MapPin, Trash2, Send, ArrowLeft,
  UserPlus, X, Phone, Mail, Search, Edit3, Check, MessageSquare, AlertCircle,
  CheckSquare, Square, ImagePlus, Link as LinkIcon, RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MERGE_FIELDS = ["{first_name}", "{last_name}"];
const SMS_MAX = 320;

const GROUP_COLORS = [
  { value: "#FF5C35", label: "Orange" },
  { value: "#3B82F6", label: "Blue" },
  { value: "#10B981", label: "Green" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#EF4444", label: "Red" },
  { value: "#06B6D4", label: "Cyan" },
  { value: "#EC4899", label: "Pink" },
];

const KIND_OPTIONS = [
  { value: "team", label: "Team", hint: "Agents, staff, vendors" },
  { value: "leads", label: "Leads", hint: "Clients and prospects" },
];

// ─── Create / Edit Group Modal ───────────────────────────────────────────────
function GroupModal({ group, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [location, setLocation] = useState(group?.location || "");
  const [color, setColor] = useState(group?.color || "#FF5C35");
  const [kind, setKind] = useState(group?.kind || "team");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (group?.id) {
        await axios.put(`${API}/api/groups/${group.id}`, { name, location, color, kind });
      } else {
        await axios.post(`${API}/api/groups`, { name, location, color, kind });
      }
      onSaved();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save group");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">{group?.id ? "Edit Group" : "Create Group"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Group Name *</label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Las Vegas Agents"
              className="input-field w-full"
              autoFocus
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Who's in this group?</label>
            <div className="grid grid-cols-2 gap-2">
              {KIND_OPTIONS.map(k => (
                <button key={k.value} type="button" onClick={() => setKind(k.value)}
                  className={`text-left p-3 rounded-lg border transition-colors ${kind === k.value ? "border-[#FF5C35] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-sm font-semibold text-slate-900">{k.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{k.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Location / Label</label>
            <input
              value={location} onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Las Vegas, NV"
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Color</label>
            <div className="flex gap-2 flex-wrap">
              {GROUP_COLORS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setColor(c.value)}
                  className={`w-8 h-8 rounded-full transition-all ${color === c.value ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : "hover:scale-105"}`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={!name.trim() || saving} className="btn-primary text-sm">
            {saving ? "Saving..." : group?.id ? "Save Changes" : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Member Modal ────────────────────────────────────────────────────────
function AddMemberModal({ groupId, onClose, onAdded }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!firstName.trim() || !phone.trim()) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/api/groups/${groupId}/members`, {
        first_name: firstName, last_name: lastName, phone, email,
      });
      onAdded();
      // Reset for quick multi-add
      setFirstName(""); setLastName(""); setPhone(""); setEmail("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add member");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Add Member</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">First Name *</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" className="input-field w-full" autoFocus />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Last Name</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" className="input-field w-full" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Phone Number *</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(702) 555-1234" className="input-field w-full" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" className="input-field w-full" />
          </div>
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} /> {error}</p>}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary text-sm">Done</button>
          <button onClick={save} disabled={!firstName.trim() || !phone.trim() || saving} className="btn-primary text-sm">
            <UserPlus size={15} /> {saving ? "Adding..." : "Add Member"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quick DM Modal ─────────────────────────────────────────────────────────
function QuickDmModal({ member, groupColor, onClose, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, or WebP"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/api/upload/image`, form);
      setImage({ url: r.data.url, filename: r.data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { alert("Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const payload = { body };
      if (image) payload.media_url = image.url;
      await axios.post(`${API}/api/groups/dm/${member.id}`, payload);
      onSent();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: groupColor }}>
              {(member.first_name?.[0] || "").toUpperCase()}{(member.last_name?.[0] || "").toUpperCase()}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Message {member.first_name} {member.last_name}</h2>
              <p className="text-xs text-slate-500">{member.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <textarea
            value={body} onChange={e => setBody(e.target.value)}
            placeholder={`Hi ${member.first_name}...`}
            rows={4} maxLength={SMS_MAX}
            className="input-field w-full resize-none"
            autoFocus
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFileUpload} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-slate-400 hover:text-slate-600 transition-colors" title="Attach image">
                {uploading ? <span className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin inline-block" /> : <ImagePlus size={16} />}
              </button>
            </div>
            <span className={`text-xs ${body.length > SMS_MAX * 0.9 ? "text-red-500" : "text-slate-400"}`}>{body.length}/{SMS_MAX}</span>
          </div>
          {image && (
            <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-2">
              <img src={image.preview} alt="" className="w-12 h-12 object-cover rounded-md" />
              <p className="text-xs text-slate-600 flex-1 truncate">{image.filename}</p>
              <button onClick={() => setImage(null)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={send} disabled={!body.trim() || sending} className="btn-primary text-sm">
            <Send size={14} /> {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Group Chat Thread ──────────────────────────────────────────────────────
function GroupChatThread({ group, messages, loadingMsgs, onRefresh }) {
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [chatMsg, setChatMsg] = useState("");
  const [chatSending, setChatSending] = useState(false);

  // Scroll to bottom when messages load or change
  useEffect(() => {
    if (chatEndRef.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendGroupChat = async () => {
    if (!chatMsg.trim() || chatSending) return;
    setChatSending(true);
    try {
      await axios.post(`${API}/api/groups/${group.id}/blast`, { body: chatMsg });
      setChatMsg("");
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send");
    }
    setChatSending(false);
  };

  // Show messages in chronological order (oldest first) for chat view
  const sorted = [...messages].reverse();

  // Group consecutive outbound messages from the same blast timestamp
  const groupedMessages = [];
  sorted.forEach((msg, i) => {
    // For outbound blasts, collapse duplicates with the same body + similar timestamp
    if (msg.direction === "outbound" && msg.source === "group_blast" && i > 0) {
      const prev = groupedMessages[groupedMessages.length - 1];
      if (prev && prev.direction === "outbound" && prev.body === msg.body && prev.source === "group_blast") {
        // Same blast, add recipient to the existing entry
        if (!prev._recipients) prev._recipients = [prev.member_name];
        prev._recipients.push(msg.member_name);
        return;
      }
    }
    groupedMessages.push({ ...msg });
  });

  if (loadingMsgs) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="card overflow-hidden flex flex-col" style={{ height: "min(600px, 70vh)" }}>
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
        <span className="text-xs text-slate-500 font-medium">
          {messages.length} message{messages.length !== 1 ? "s" : ""} in thread
        </span>
        <button onClick={onRefresh} className="text-slate-400 hover:text-slate-600 transition-colors" title="Refresh">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Chat messages area */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50/30">
        {groupedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <MessageSquare size={32} className="mx-auto mb-3 text-slate-300" />
            <h3 className="text-slate-900 font-semibold mb-1">No Messages Yet</h3>
            <p className="text-slate-500 text-sm">Send a message to start the group conversation.</p>
          </div>
        ) : (
          groupedMessages.map((msg, idx) => {
            const isOutbound = msg.direction === "outbound";
            const time = new Date(msg.created_at).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
            });
            return (
              <div key={msg.id || idx} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] ${isOutbound ? "order-2" : ""}`}>
                  {/* Sender name for inbound */}
                  {!isOutbound && (
                    <div className="flex items-center gap-1.5 mb-0.5 ml-1">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: "#10B981" }}>
                        {(msg.member_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs font-semibold text-slate-700">{msg.member_name || msg.phone}</span>
                    </div>
                  )}
                  {/* Blast label for outbound */}
                  {isOutbound && msg.source === "group_blast" && (
                    <div className="flex items-center justify-end gap-1 mb-0.5 mr-1">
                      <span className="text-[10px] text-slate-400">
                        Sent to {msg._recipients ? `${msg._recipients.length} members` : msg.member_name}
                      </span>
                    </div>
                  )}
                  {isOutbound && msg.source === "group_dm" && (
                    <div className="flex items-center justify-end gap-1 mb-0.5 mr-1">
                      <span className="text-[10px] text-slate-400">DM to {msg.member_name}</span>
                    </div>
                  )}
                  {/* Message bubble */}
                  <div className={`rounded-2xl px-3.5 py-2 ${
                    isOutbound
                      ? "text-white rounded-br-md"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                  }`} style={isOutbound ? { backgroundColor: group.color || "#FF5C35" } : undefined}>
                    <p className="text-sm break-words whitespace-pre-wrap">{msg.body}</p>
                    {msg.media_url && (
                      <img src={msg.media_url} alt="" className="mt-1.5 rounded-lg max-h-40 max-w-full object-cover" />
                    )}
                  </div>
                  {/* Timestamp + status */}
                  <div className={`flex items-center gap-1.5 mt-0.5 ${isOutbound ? "justify-end mr-1" : "ml-1"}`}>
                    <span className="text-[10px] text-slate-400">{time}</span>
                    {isOutbound && (
                      <span className={`text-[10px] ${
                        msg.status === "delivered" ? "text-green-500" :
                        msg.status === "failed" ? "text-red-400" :
                        msg.status === "mock" ? "text-amber-400" :
                        "text-slate-400"
                      }`}>{msg.status === "delivered" ? "✓✓" : msg.status === "queued" ? "✓" : msg.status === "failed" ? "✗" : ""}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Compose bar */}
      <div className="border-t border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <input
            value={chatMsg}
            onChange={e => setChatMsg(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendGroupChat(); } }}
            placeholder="Message group..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-slate-200 transition-all"
          />
          <button
            onClick={sendGroupChat}
            disabled={!chatMsg.trim() || chatSending}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-40"
            style={{ backgroundColor: group.color || "#FF5C35" }}
          >
            <Send size={14} />
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1 ml-3">Sends to all group members as a blast</p>
      </div>
    </div>
  );
}

// ─── Group Detail View ───────────────────────────────────────────────────────
function GroupDetail({ group, onBack, onRefresh, initialTab }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [search, setSearch] = useState("");
  const [blastBody, setBlastBody] = useState("");
  const [sending, setSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [showComposer, setShowComposer] = useState(false);
  const [detailTab, setDetailTab] = useState(initialTab || "members"); // "members" or "history"
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [dmMember, setDmMember] = useState(null);
  const [blastImage, setBlastImage] = useState(null);
  const [blastUploading, setBlastUploading] = useState(false);
  const blastFileRef = useRef(null);

  const toggleMember = (id) => setSelectedMembers(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAllMembers = () => setSelectedMembers(members.map(m => m.id));
  const clearAllMembers = () => setSelectedMembers([]);

  const handleBlastFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, or WebP"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setBlastUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/api/upload/image`, form);
      setBlastImage({ url: r.data.url, filename: r.data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { alert("Upload failed"); }
    setBlastUploading(false);
    if (blastFileRef.current) blastFileRef.current.value = "";
  };

  const loadMessages = async () => {
    setLoadingMsgs(true);
    try {
      const res = await axios.get(`${API}/api/groups/${group.id}/messages`);
      setMessages(res.data);
    } catch (e) { console.error(e); }
    setLoadingMsgs(false);
  };

  const loadMembers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/groups/${group.id}/members`);
      setMembers(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadMembers(); loadMessages(); }, [group.id]);

  const removeMember = async (memberId) => {
    if (!window.confirm("Remove this member from the group?")) return;
    try {
      await axios.delete(`${API}/api/groups/${group.id}/members/${memberId}`);
      loadMembers();
      onRefresh();
    } catch (e) { alert("Failed to remove member"); }
  };

  const sendBlast = async () => {
    if (!blastBody.trim()) return;
    const targetCount = selectedMembers.length > 0 ? selectedMembers.length : members.length;
    if (targetCount === 0) return;
    setSending(true);
    setBlastResult(null);
    try {
      const payload = { body: blastBody };
      if (blastImage) payload.media_url = blastImage.url;
      if (selectedMembers.length > 0 && selectedMembers.length < members.length) {
        payload.member_ids = selectedMembers;
      }
      const res = await axios.post(`${API}/api/groups/${group.id}/blast`, payload);
      setBlastResult(res.data);
      setBlastBody("");
      setBlastImage(null);
      loadMessages();
    } catch (e) {
      setBlastResult({ error: e.response?.data?.detail || "Failed to send" });
    }
    setSending(false);
  };

  const filtered = search
    ? members.filter(m => `${m.first_name} ${m.last_name} ${m.phone} ${m.email}`.toLowerCase().includes(search.toLowerCase()))
    : members;

  const charCount = blastBody.length;

  return (
    <div className="p-4 md:p-8">
      {showAddMember && (
        <AddMemberModal
          groupId={group.id}
          onClose={() => setShowAddMember(false)}
          onAdded={() => { loadMembers(); onRefresh(); }}
        />
      )}
      {dmMember && (
        <QuickDmModal
          member={dmMember}
          groupColor={group.color}
          onClose={() => setDmMember(null)}
          onSent={() => { loadMessages(); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="btn-secondary text-sm p-2">
          <ArrowLeft size={16} />
        </button>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: group.color + "20" }}>
            <FolderOpen size={20} style={{ color: group.color }} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900 truncate">{group.name}</h1>
            {group.location && (
              <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={12} /> {group.location}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowComposer(!showComposer)} className="btn-primary text-sm">
            <Send size={15} /> Send Blast
          </button>
          <button onClick={() => setShowAddMember(true)} className="btn-secondary text-sm">
            <UserPlus size={15} /> <span className="hidden sm:inline">Add Member</span>
          </button>
        </div>
      </div>

      {/* Blast Composer */}
      {showComposer && (
        <div className="card mb-6">
          <div className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <MessageSquare size={16} style={{ color: group.color }} /> Send Message to {selectedMembers.length > 0 ? `${selectedMembers.length} Selected` : `All ${members.length}`} Members
            </h3>
            {selectedMembers.length > 0 && selectedMembers.length < members.length && (
              <p className="text-xs text-amber-600 mb-2">Sending to {selectedMembers.length} of {members.length} members (select members below)</p>
            )}
            <textarea
              value={blastBody}
              onChange={e => setBlastBody(e.target.value)}
              placeholder={`Hi {first_name}, ...`}
              rows={4}
              maxLength={SMS_MAX}
              className="input-field w-full resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex gap-1.5 flex-wrap">
                {MERGE_FIELDS.map(f => (
                  <button key={f} onClick={() => setBlastBody(b => b + f)}
                    className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
                    {f}
                  </button>
                ))}
              </div>
              <span className={`text-xs ${charCount > SMS_MAX * 0.9 ? "text-red-500" : "text-slate-400"}`}>{charCount}/{SMS_MAX}</span>
            </div>
            {/* Image attach */}
            <input type="file" ref={blastFileRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleBlastFileUpload} />
            <div className="mt-3 pt-3 border-t border-slate-100">
              {blastImage ? (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-2 mb-3">
                  <img src={blastImage.preview} alt="" className="w-12 h-12 object-cover rounded-md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{blastImage.filename}</p>
                    <p className="text-[10px] text-slate-400">Will send as MMS</p>
                  </div>
                  <button onClick={() => setBlastImage(null)} className="text-slate-400 hover:text-red-500 shrink-0"><X size={14} /></button>
                </div>
              ) : (
                <button
                  onClick={() => blastFileRef.current?.click()}
                  disabled={blastUploading}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3"
                >
                  {blastUploading ? <span className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" /> : <ImagePlus size={14} />}
                  Attach Image (MMS)
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={sendBlast} disabled={!blastBody.trim() || sending || (selectedMembers.length === 0 && members.length === 0)}
                className="btn-primary text-sm">
                <Send size={14} /> {sending ? "Sending..." : `Send to ${selectedMembers.length > 0 ? selectedMembers.length : members.length} Members`}
              </button>
              <button onClick={() => { setShowComposer(false); setBlastResult(null); setBlastImage(null); }} className="btn-secondary text-sm">Cancel</button>
            </div>
            {blastResult && !blastResult.error && (
              <div className={`mt-3 p-3 rounded-lg border text-sm ${blastResult.failed > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
                <div className={blastResult.failed > 0 ? "text-amber-700" : "text-green-700"}>
                  <Check size={14} className="inline mr-1" />
                  Sent to {blastResult.sent} of {blastResult.total} members
                  {blastResult.failed > 0 && <span className="text-red-600 ml-2">({blastResult.failed} failed)</span>}
                  {blastResult.mock && <span className="text-amber-600 ml-2">(Mock mode)</span>}
                </div>
                {blastResult.failures && blastResult.failures.length > 0 && (
                  <div className="mt-2 border-t border-red-200 pt-2">
                    <p className="text-xs font-semibold text-red-600 mb-1">Failed deliveries:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {blastResult.failures.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                          <X size={10} className="shrink-0" />
                          <span className="font-medium">{f.name}</span>
                          {f.phone && <span className="text-red-400">{f.phone}</span>}
                          <span className="text-red-400 ml-auto truncate max-w-[200px]">— {f.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {blastResult?.error && (
              <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <AlertCircle size={14} className="inline mr-1" /> {blastResult.error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs: Members / Message History */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-0.5 max-w-xs">
        <button
          onClick={() => setDetailTab("members")}
          className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${detailTab === "members" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        ><Users size={13} /> Members ({members.length})</button>
        <button
          onClick={() => { setDetailTab("history"); if (messages.length === 0) loadMessages(); }}
          className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${detailTab === "history" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        ><MessageSquare size={13} /> Messages ({messages.length})</button>
      </div>

      {detailTab === "members" ? (
        <>
          {/* Search */}
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search members..."
              className="input-field w-full pl-9"
            />
          </div>

          {/* Members list */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="card">
              <div className="p-12 text-center">
                <UserPlus size={32} className="mx-auto mb-3 text-slate-300" />
                <h3 className="text-slate-900 font-semibold mb-1">{search ? "No members match your search" : "No Members Yet"}</h3>
                <p className="text-slate-500 text-sm mb-4">{search ? "Try a different search term" : "Add members to this group to start sending texts."}</p>
                {!search && (
                  <button onClick={() => setShowAddMember(true)} className="btn-primary text-sm inline-flex">
                    <UserPlus size={15} /> Add First Member
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="card overflow-hidden">
              {/* Select / Clear bar */}
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
                <p className="text-xs text-slate-500">
                  {selectedMembers.length > 0 ? <span className="font-semibold" style={{ color: group.color }}>{selectedMembers.length} selected</span> : `${filtered.length} members`}
                </p>
                <div className="flex gap-2">
                  <button onClick={selectAllMembers} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md text-white transition-colors" style={{ backgroundColor: group.color }}>
                    <CheckSquare size={10} /> All
                  </button>
                  <button onClick={clearAllMembers} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-200 text-slate-700 text-[10px] font-semibold rounded-md hover:bg-slate-300 transition-colors">
                    <X size={10} /> Clear
                  </button>
                  {selectedMembers.length > 0 && (
                    <button onClick={() => setShowComposer(true)} className="inline-flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-[10px] font-semibold rounded-md hover:bg-green-600 transition-colors">
                      <Send size={10} /> Message Selected
                    </button>
                  )}
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100" style={{ backgroundColor: "#F8F9FA" }}>
                    <th className="w-10 px-3 py-3"></th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Name</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Phone</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Email</th>
                    <th className="w-20 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(m => (
                    <tr key={m.id} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${selectedMembers.includes(m.id) ? "bg-orange-50/50" : ""}`}>
                      <td className="px-3 py-3">
                        <button onClick={() => toggleMember(m.id)} className="text-slate-400 hover:text-slate-600">
                          {selectedMembers.includes(m.id) ? <CheckSquare size={16} style={{ color: group.color }} /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
                            style={{ backgroundColor: group.color }}>
                            {(m.first_name?.[0] || "").toUpperCase()}{(m.last_name?.[0] || "").toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{m.first_name} {m.last_name}</p>
                            <p className="text-xs text-slate-500 sm:hidden flex items-center gap-1"><Phone size={10} /> {m.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden sm:table-cell">
                        <span className="flex items-center gap-1"><Phone size={12} className="text-slate-400" /> {m.phone}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">
                        {m.email ? <span className="flex items-center gap-1"><Mail size={12} className="text-slate-400" /> {m.email}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setDmMember(m)} className="text-slate-400 hover:text-blue-500 transition-colors" title="Send message">
                            <MessageSquare size={14} />
                          </button>
                          <button onClick={() => removeMember(m.id)} className="text-slate-400 hover:text-red-500 transition-colors" title="Remove">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        /* Group Chat Thread */
        <GroupChatThread
          group={group}
          messages={messages}
          loadingMsgs={loadingMsgs}
          onRefresh={loadMessages}
        />
      )}
    </div>
  );
}

// ─── Main Groups Page ────────────────────────────────────────────────────────
export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");

  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/groups`);
      setGroups(res.data);
      // Deep link from the inbox: /groups?open=<id> opens that group's conversation
      if (openId) {
        const g = res.data.find(x => x.id === openId);
        if (g) setSelectedGroup({ ...g, _fromInbox: true });
        setSearchParams({}, { replace: true });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadGroups(); }, []);

  const deleteGroup = async (e, groupId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this group and all its members? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/groups/${groupId}`);
      loadGroups();
    } catch (e) { alert("Failed to delete group"); }
  };

  // If a group is selected, show its detail view
  if (selectedGroup) {
    return (
      <GroupDetail
        group={selectedGroup}
        initialTab={selectedGroup._fromInbox ? "history" : "members"}
        onBack={() => { setSelectedGroup(null); loadGroups(); }}
        onRefresh={loadGroups}
      />
    );
  }

  return (
    <div className="p-4 md:p-8">
      {(showCreateModal || editGroup) && (
        <GroupModal
          group={editGroup}
          onClose={() => { setShowCreateModal(false); setEditGroup(null); }}
          onSaved={loadGroups}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Groups</h1>
          <p className="text-slate-500 text-sm mt-0.5">Organize contacts into groups for easy mass texting</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm">
          <Plus size={16} /> Create Group
        </button>
      </div>

      {/* Groups grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
        </div>
      ) : groups.length === 0 ? (
        <div className="card">
          <div className="p-16 text-center">
            <FolderOpen size={40} className="mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Groups Yet</h3>
            <p className="text-slate-500 text-sm mb-6 max-w-sm mx-auto">
              Create groups to organize agents, vendors, or any contacts you want to text separately from your FUB leads.
            </p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm inline-flex">
              <Plus size={16} /> Create Your First Group
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
        {["team", "leads"].map(kindKey => {
          const list = groups.filter(g => (g.kind || "team") === kindKey);
          if (!list.length) return null;
          return (
        <div key={kindKey}>
          <div className="flex items-baseline gap-2 mb-3">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{kindKey === "team" ? "Team groups" : "Lead groups"}</h2>
            <span className="text-xs text-slate-500">{kindKey === "team" ? "Agents, staff, vendors" : "Client and prospect segments"}</span>
          </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(g => (
            <div
              key={g.id}
              onClick={() => setSelectedGroup(g)}
              className="card cursor-pointer hover:shadow-md transition-all group"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: g.color + "20" }}>
                      <FolderOpen size={20} style={{ color: g.color }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{g.name}</h3>
                      {g.location && (
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <MapPin size={10} /> {g.location}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditGroup(g); }}
                      className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      title="Edit"
                    >
                      <Edit3 size={13} />
                    </button>
                    <button
                      onClick={(e) => deleteGroup(e, g.id)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Users size={13} /> {g.member_count || 0} members
                  </span>
                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: g.color + "15", color: g.color }}>
                    View →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
          );
        })}
        </div>
      )}
    </div>
  );
}
